import { useEffect, useState } from "react";
import {
    collection,
    doc,
    addDoc,
    getDoc,
    setDoc,
    deleteDoc,
    updateDoc,
    onSnapshot,
    serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { CLUB_ID, THEME_COLOR } from "./config/club";
import { useAuth } from "./context/AuthContext";
import Swal from "sweetalert2";
import AppHeader from "./components/layout/AppHeader";
import BottomNav from "./components/layout/BottomNav";
import { useNavigate } from "react-router-dom";
import GlobalAdsModal from "./components/ads/GlobalAdsModal";

const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));

    return new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    }).format(date);
};

const formatDate = (dateStr) => {
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    }).format(new Date(dateStr));
};

export default function ReservationsPage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const [reservations, setReservations] = useState([]);
    const [playersMap, setPlayersMap] = useState({});
    const [expanded, setExpanded] = useState(null);

    // 🔥 REALTIME LISTENER
    useEffect(() => {
        const reservationsRef = collection(db, "clubs", CLUB_ID, "reservations");

        const playerUnsubs = {}; // map by reservationId

        const unsubReservations = onSnapshot(reservationsRef, (snap) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const list = snap.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter((r) => {
                    if (!r.date) return false;

                    const reservationDate = new Date(r.date);
                    reservationDate.setHours(0, 0, 0, 0);

                    return reservationDate >= today;
                })
                .sort((a, b) => {
                    const aDateTime = new Date(`${a.date}T${a.startTime || "00:00"}`).getTime();
                    const bDateTime = new Date(`${b.date}T${b.startTime || "00:00"}`).getTime();

                    return aDateTime - bDateTime; // upcoming first
                });
            setReservations(list);

            const currentIds = new Set(list.map(r => r.id));

            // 🔥 REMOVE listeners that no longer exist
            Object.keys(playerUnsubs).forEach((id) => {
                if (!currentIds.has(id)) {
                    playerUnsubs[id]();
                    delete playerUnsubs[id];
                }
            });

            // 🔥 ADD only new listeners
            list.forEach((r) => {
                if (playerUnsubs[r.id]) return; // already listening

                const playersRef = collection(
                    db,
                    "clubs",
                    CLUB_ID,
                    "reservations",
                    r.id,
                    "players"
                );

                playerUnsubs[r.id] = onSnapshot(playersRef, (playersSnap) => {
                    const players = playersSnap.docs
                        .map((d) => ({
                            id: d.id,
                            ...d.data(),
                        }))
                        .sort(
                            (a, b) =>
                                (a.joinedAt?.toMillis?.() || 0) -
                                (b.joinedAt?.toMillis?.() || 0)
                        );

                    setPlayersMap((prev) => ({
                        ...prev,
                        [r.id]: players,
                    }));
                });
            });
        });

        return () => {
            unsubReservations();
            Object.values(playerUnsubs).forEach((u) => u());
        };
    }, []);

    // 🔥 JOIN
    const handleJoin = async (r) => {
        if (!user) return Swal.fire("Login required", "", "info");

        const result = await Swal.fire({
            title: "Join Game?",
            html: `
            <p>You are about to join this game.</p>
            <div class="alert alert-warning text-start mt-3 mb-0">
                <strong>Notice:</strong> Players who <strong>do not show up</strong> or <strong>cancel on short notice</strong> will be required to pay <strong>100% of the game fee</strong>.
            </div>
        `,
            icon: "info",
            showCancelButton: true,
            confirmButtonText: "Join",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        try {
            const ref = doc(
                db,
                "clubs",
                CLUB_ID,
                "reservations",
                r.id,
                "players",
                user.uid
            );

            const existing = await getDoc(ref);
            if (existing.exists()) {
                return Swal.fire("Already joined", "", "info");
            }

            const players = playersMap[r.id] || [];
            const reservedCount = players.filter(
                (p) => p.status === "reserved"
            ).length;

            const isFull = reservedCount >= r.maxSlots;

            await setDoc(ref, {
                uid: user.uid,
                displayName: user.displayName || "Player",
                realName: user.realName || "",
                photoURL: user.photoURL || "",
                level: user.level || 1,
                username: user.username || null,
                status: isFull ? "waitlisted" : "reserved",
                joinedAt: serverTimestamp(),
            });

            Swal.fire(
                "Success",
                isFull ? "Added to waitlist." : "Slot reserved!",
                "success"
            );
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Something went wrong", "error");
        }
    };

    // 🔥 LEAVE + AUTO PROMOTE
    const handleLeave = async (r) => {
        const confirm = await Swal.fire({
            title: "Leave?",
            text: "Give others a chance to play!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: THEME_COLOR,
            cancelButtonColor: "#aaa",
        });

        if (!confirm.isConfirmed) return;

        const ref = doc(
            db,
            "clubs",
            CLUB_ID,
            "reservations",
            r.id,
            "players",
            user.uid
        );

        const players = playersMap[r.id] || [];
        const me = players.find((p) => p.id === user.uid);

        // ✅ CREATE LOG
        await addDoc(
            collection(
                db,
                "clubs",
                CLUB_ID,
                "reservations",
                r.id,
                "logs"
            ),
            {
                type: "leave",
                playerId: user.uid,
                playerName: me?.name || user.displayName || "Unknown",
                statusBeforeLeave: me?.status || null,
                createdAt: serverTimestamp(),
            }
        );

        // ✅ REMOVE PLAYER
        await deleteDoc(ref);

        // 🔥 AUTO PROMOTE (FIFO)
        // if (me?.status === "reserved") {
        //     const waitlist = players
        //         .filter((p) => p.status === "waitlisted")
        //         .sort(
        //             (a, b) =>
        //                 (a.joinedAt?.toMillis?.() || 0) -
        //                 (b.joinedAt?.toMillis?.() || 0)
        //         );

        //     if (waitlist.length > 0) {
        //         await updateDoc(
        //             doc(
        //                 db,
        //                 "clubs",
        //                 CLUB_ID,
        //                 "reservations",
        //                 r.id,
        //                 "players",
        //                 waitlist[0].id
        //             ),
        //             { status: "reserved" }
        //         );
        //     }
        // }
    };

    return (
        <div className="min-vh-100 pb-5">
            <AppHeader
                user={user}
                title="Regular Scheduled Games"
                subtitle="Secure your spot"
                logout={logout}
                clubId={CLUB_ID}
                sessionId={null}
            />
            <GlobalAdsModal
                clubId={CLUB_ID}
                themeColor={THEME_COLOR}
                sessionKey={'reservations'}
            />
            <div
                className="container px-3 mt-n4"
                style={{ maxWidth: 500 }}
            >
                <br></br>
                <div
                    className="w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                    style={{
                        background: `linear-gradient(90deg, transparent, color-mix(in srgb, ${THEME_COLOR}, transparent 94%), transparent)`,
                        borderBottom: `1px solid var(--border-color)`,
                        marginTop: '-1rem', // Pulls it slightly up to align with the header if needed
                        marginBottom: '1.5rem'
                    }}
                >
                    {/* Minimal Pulse Dot */}
                    <span className="position-relative d-flex" style={{ width: '6px', height: '6px' }}>
                        <span className="animate-ping position-absolute h-100 w-100 rounded-circle opacity-75" style={{ background: '#ffc107' }}></span>
                        <span className="relative rounded-circle h-100 w-100" style={{ background: '#ffc107' }}></span>
                    </span>

                    <span className="rank-label" style={{
                        fontSize: '0.6rem',
                        letterSpacing: '1.2px',
                        color: 'var(--muted-text)',
                        marginBottom: 0
                    }}>
                        Feature in <span style={{ color: THEME_COLOR, fontWeight: '900' }}>Beta Testing</span>
                    </span>
                </div>
                {reservations.length === 0 ? (
                    /* RESERVATIONS EMPTY FALLBACK */
                    <div
                        className="bento-card mb-3 shadow-sm d-flex flex-column align-items-center justify-content-center py-5 text-center"
                        style={{ border: "1px dashed var(--border-color)" }}
                    >
                        <div
                            className="mb-3 d-flex align-items-center justify-content-center rounded-circle"
                            style={{ width: 64, height: 64, background: "var(--surface-bg)", color: "var(--muted-text)", fontSize: "1.75rem" }}
                        >
                            <i className="bi bi-calendar-x"></i>
                        </div>
                        <h5 className="fw-black mb-1">No Active Sessions</h5>
                        <p className="text-muted small px-3 mb-0" style={{ maxWidth: 280 }}>
                            There are currently no scheduled reservations available. Check back later!
                        </p>
                    </div>
                ) : (
                    reservations.map((r) => {
                        const players = playersMap[r.id] || [];
                        const reserved = players.filter(
                            (p) => p.status === "reserved"
                        );
                        const waitlist = players.filter(
                            (p) => p.status === "waitlisted"
                        );

                        const isJoined = players.some(
                            (p) => p.id === user?.uid
                        );
                        const myStatus = players.find(
                            (p) => p.id === user?.uid
                        )?.status;

                        const isFull = reserved.length >= r.maxSlots;
                        const rStatus = r.status;
                        const isClosed = rStatus?.toLowerCase() === "closed" || rStatus?.toLowerCase() === "full";
                        //console.log(rStatus);
                        return (
                            <div
                                key={r.id}
                                className="bento-card mb-3 shadow-sm"
                            >
                                <div className="card-body p-4">
                                    {/* HEADER */}
                                    <div className="d-flex justify-content-between mb-3">
                                        <div>
                                            <h5 className="fw-black mb-1">
                                                {r.name}
                                            </h5>
                                            <small className="text-muted">
                                                {formatDate(r.date)} •{" "}
                                                {formatTime(r.startTime)} -{" "}
                                                {formatTime(r.endTime)}
                                            </small>
                                        </div>

                                        <div className="d-flex flex-column align-items-end">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="fw-black tracking-wider" style={{ color: THEME_COLOR, fontSize: '1.1rem' }}>
                                                    {reserved.length}
                                                    <span className="opacity-25 mx-1" style={{ fontWeight: 300, color: 'var(--text-color)' }}>/</span>
                                                    <span className="opacity-30" style={{ fontSize: '0.85rem' }}>{r.maxSlots}</span>
                                                </span>
                                            </div>

                                            {/* Micro Progress Bar */}
                                            <div className="overflow-hidden rounded-pill" style={{ width: '40px', height: '4px', background: 'var(--border-color)' }}>
                                                <div
                                                    className="h-100 rounded-pill"
                                                    style={{
                                                        width: `${r.maxSlots ? (reserved.length / r.maxSlots) * 100 : 0}%`,
                                                        transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        background: THEME_COLOR,
                                                        boxShadow: `0 0 8px ${THEME_COLOR}66`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* AVATAR STACK */}
                                    <div
                                        className="mb-3 d-flex align-items-center justify-content-between p-2 rounded-3"
                                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                                        style={{ cursor: window.innerWidth > 768 ? "pointer" : "default", background: "rgba(0,0,0,0.02)" }}
                                    >
                                        {/* Left Side: Avatar Stack */}
                                        <div className="d-flex align-items-center">
                                            <div className="d-flex me-2">
                                                {reserved.slice(0, 6).map((p, i) => (
                                                    <img
                                                        key={p.id}
                                                        src={p.photoURL || `https://ui-avatars.com/api/?name=${p.displayName}&background=random`}
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: 10,
                                                            marginLeft: i ? -12 : 0, // Increased overlap for a tighter look
                                                            border: "2px solid white",
                                                            objectFit: "cover",
                                                            boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
                                                            zIndex: 10 - i, // Ensures first avatar is always on top
                                                        }}
                                                        alt=""
                                                    />
                                                ))}

                                                {/* Show counter if more than 6 players */}
                                                {reserved.length > 6 && (
                                                    <div
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: 10,
                                                            marginLeft: -12,
                                                            border: "2px solid white",
                                                            backgroundColor: "#e9ecef",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: "0.7rem",
                                                            fontWeight: "bold",
                                                            color: "#6c757d",
                                                            zIndex: 0
                                                        }}
                                                    >
                                                        +{reserved.length - 6}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Player Count Label */}
                                            <span className="small text-muted fw-medium">
                                                {reserved.length} {reserved.length === 1 ? 'Player' : 'Players'}
                                            </span>
                                        </div>

                                        {/* Right Side: Toggle Action */}
                                        <div
                                            className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill transition-all"
                                            style={{
                                                background: expanded === r.id ? 'var(--surface-bg)' : 'transparent',
                                                border: `1px solid ${expanded === r.id ? 'var(--border-color)' : 'transparent'}`,
                                            }}
                                        >
                                            <small
                                                className="fw-black text-uppercase tracking-wider"
                                                style={{
                                                    fontSize: '0.65rem',
                                                    color: THEME_COLOR,
                                                    letterSpacing: '0.8px'
                                                }}
                                            >
                                                {expanded === r.id ? 'Close' : 'View All'}
                                            </small>

                                            <i className={`bi bi-chevron-right transition-all d-inline-block`}
                                                style={{
                                                    fontSize: '0.65rem',
                                                    color: THEME_COLOR,
                                                    transform: expanded === r.id ? 'rotate(90deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.3s ease'
                                                }}
                                            ></i>
                                        </div>
                                    </div>

                                    {/* EXPANDED */}
                                    {expanded === r.id && (
                                        <div className="mt-3 animate-fade-in">
                                            {/* HEADER */}
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <span className="rank-label" style={{ color: 'var(--muted-text)' }}>Confirmed Players</span>
                                                <span className="badge rounded-pill" style={{ background: `color-mix(in srgb, ${THEME_COLOR}, transparent 85%)`, color: THEME_COLOR }}>
                                                    {reserved.length} Players
                                                </span>
                                            </div>

                                            {reserved.length === 0 ? (
                                                /* EMPTY STATE */
                                                <div className="d-flex flex-column align-items-center justify-content-center py-5 rounded-4"
                                                    style={{ background: "var(--surface-bg)", border: "1px dashed var(--border-color)" }}>
                                                    <div className="mb-3 d-flex align-items-center justify-content-center rounded-circle"
                                                        style={{ width: 56, height: 56, background: "var(--card-bg)", color: "var(--muted-text)", fontSize: "1.5rem" }}>
                                                        <i className="bi bi-person-plus"></i>
                                                    </div>
                                                    <div className="fw-black small text-uppercase tracking-wider mb-1">No players yet</div>
                                                    <div className="text-muted text-center" style={{ fontSize: "0.7rem", maxWidth: 200 }}>
                                                        Be the first to step onto the court. Join this session now!
                                                    </div>
                                                </div>
                                            ) : (
                                                /* PLAYER LIST */
                                                <div className="rounded-4 overflow-hidden" style={{ background: "var(--surface-bg)", border: "1px solid var(--border-color)" }}>
                                                    <div style={{ maxHeight: 350, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", touchAction: "pan-y" }}>
                                                        {reserved.map((p, index) => (
                                                            <div key={p.id}
                                                                className="d-flex align-items-center gap-3 p-3 list-item-player"
                                                                style={{
                                                                    borderBottom: index === reserved.length - 1 ? 'none' : '1px solid var(--border-color)',
                                                                    cursor: window.innerWidth > 768 ? "pointer" : "default"
                                                                }}
                                                                onClick={(e) => {
                                                                    if (e.defaultPrevented) return;
                                                                    setTimeout(() => {
                                                                        p.username
                                                                            ? navigate(`/u/${p.username}`)
                                                                            : Swal.fire({ icon: "info", title: "No profile", text: "User profile pending." });
                                                                    }, 50);
                                                                }}
                                                            >

                                                                <div className="position-relative">
                                                                    <img src={p.photoURL || '/img/rrc.png'} className="rounded-3 shadow-sm"
                                                                        style={{ width: 36, height: 36, objectFit: "cover", border: `1px solid var(--border-color)` }} />
                                                                </div>

                                                                <div className="d-flex flex-column">
                                                                    <span className="fw-bold small" style={{ color: "var(--text-color)" }}>{p.displayName || p.realName}</span>
                                                                    {p.joinedAt && (
                                                                        <span className="text-muted" style={{ fontSize: '0.65rem' }}>
                                                                            Joined {p.joinedAt.toDate().toLocaleString([], {
                                                                                month: 'short',
                                                                                day: 'numeric',
                                                                                hour: '2-digit',
                                                                                minute: '2-digit'
                                                                            })}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <i className="bi bi-chevron-right ms-auto opacity-25" style={{ fontSize: '0.7rem' }}></i>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* WAITLIST SECTION */}
                                            {waitlist.length > 0 && (
                                                <div className="mt-4 p-3 rounded-4" style={{ background: 'rgba(255, 193, 7, 0.05)', border: '1px solid rgba(255, 193, 7, 0.1)' }}>
                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                        <i className="bi bi-hourglass-split text-warning"></i>
                                                        <span className="rank-label mb-0 text-warning">Waitlist</span>
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {waitlist.map((p) => (
                                                            <span key={p.id} className="badge bg-white text-dark border shadow-sm fw-bold" style={{ fontSize: '0.65rem', padding: '6px 10px' }}>
                                                                {p.displayName || p.realName}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* ACTION */}
                                    {isJoined ? (
                                        <>
                                            <div className="mt-4 p-3 rounded-4 d-flex align-items-center justify-content-between shadow-sm position-relative overflow-hidden"
                                                style={{
                                                    background: "var(--surface-bg)",
                                                    border: "1px solid var(--border-color)",
                                                    transition: "all 0.3s ease"
                                                }}>

                                                {/* Subtle Background Glow for Active State */}
                                                <div className="position-absolute" style={{
                                                    top: '-20px',
                                                    right: '-20px',
                                                    width: '80px',
                                                    height: '80px',
                                                    background: myStatus === 'waitlisted' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(89, 16, 148, 0.1)', // #591094 brand color
                                                    filter: 'blur(30px)',
                                                    borderRadius: '50%'
                                                }}></div>

                                                {/* LEFT: STATUS INFO */}
                                                <div className="d-flex align-items-center gap-3" style={{ zIndex: 1 }}>
                                                    <div className="d-flex align-items-center justify-content-center rounded-circle"
                                                        style={{
                                                            width: 40,
                                                            height: 40,
                                                            background: THEME_COLOR,
                                                            border: "1px solid var(--border-color)",
                                                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                                        }}>
                                                        {myStatus === 'waitlisted' ? (
                                                            <i className="bi bi-hourglass-split text-warning fs-5"></i>
                                                        ) : (
                                                            <i className="bi bi-check-lg fs-4" style={{ color: "white" }}></i>
                                                        )}
                                                    </div>

                                                    <div className="d-flex flex-column">
                                                        <span className="rank-label" style={{ color: "var(--muted-text)" }}>
                                                            Status
                                                        </span>
                                                        <span className="fw-black" style={{ fontSize: '0.9rem', color: "var(--text-color)" }}>
                                                            {myStatus === "waitlisted" ? "On Waitlist" : "Slot Reserved"}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* RIGHT: LEAVE ACTION */}
                                                <button
                                                    className="btn btn-sm px-3 rounded-pill fw-bold transition-all"
                                                    disabled={isClosed}
                                                    onClick={() => handleLeave(r)}
                                                    style={{
                                                        background: "transparent",
                                                        color: isClosed ? (THEME_COLOR) : ("#dc3545"),
                                                        border: "1px solid rgba(220, 53, 69, 0.2)",
                                                        fontSize: '0.75rem',
                                                        zIndex: 1
                                                    }}
                                                    onMouseOver={(e) => {
                                                        e.currentTarget.style.background = "rgba(220, 53, 69, 0.1)";
                                                        e.currentTarget.style.borderColor = "#dc3545";
                                                    }}
                                                    onMouseOut={(e) => {
                                                        e.currentTarget.style.background = "transparent";
                                                        e.currentTarget.style.borderColor = "rgba(220, 53, 69, 0.2)";
                                                    }}
                                                >
                                                    {isClosed ? ('Locked') : ('Leave')}

                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <button
                                            disabled={isClosed}
                                            className="btn w-100 mt-4 rounded-pill text-white fw-black shadow-sm d-flex align-items-center justify-content-center gap-2 border-0"
                                            style={{
                                                padding: '16px',
                                                fontSize: '1rem',
                                                letterSpacing: '0.5px',
                                                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                cursor: isClosed ? 'not-allowed' : 'pointer',
                                                opacity: isClosed ? 0.6 : 1,
                                                background: isClosed
                                                    ? '#6c757d'
                                                    : isFull
                                                        ? 'linear-gradient(135deg, #FFD700 0%, #ffc107 100%)'
                                                        : `linear-gradient(135deg, color-mix(in srgb, ${THEME_COLOR}, white 20%) 0%, ${THEME_COLOR} 100%)`,
                                                boxShadow: isClosed
                                                    ? 'none'
                                                    : isFull
                                                        ? '0 6px 20px rgba(255, 193, 7, 0.3)'
                                                        : `0 6px 20px color-mix(in srgb, ${THEME_COLOR}, transparent 70%)`
                                            }}
                                            onClick={() => {
                                                if (!isClosed) handleJoin(r);
                                            }}
                                            onMouseOver={(e) => {
                                                if (isClosed) return;
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.filter = 'brightness(1.1)';
                                            }}
                                            onMouseOut={(e) => {
                                                if (isClosed) return;
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.filter = 'brightness(1)';
                                            }}
                                        >
                                            <i className={`bi ${isClosed
                                                ? 'bi-lock-fill'
                                                : isFull
                                                    ? 'bi-hourglass-split'
                                                    : 'bi-plus-circle-fill'
                                                }`}></i>

                                            <span className="text-uppercase" style={{ fontSize: '0.85rem' }}>
                                                {isClosed
                                                    ? "Reservation Closed"
                                                    : isFull
                                                        ? "Join the Waitlist"
                                                        : "Secure My Spot"}
                                            </span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    }))}
            </div>

            <BottomNav />
        </div>
    );
}